// Leaflet is bundled with the app with OpenStreetMap + CartoDB tiles.
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix Leaflet's default icon URL path rewriting
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

(window as unknown as { L: typeof L }).L = L;

export { L };

/**
 * Standard open-source OpenStreetMap tile layer.
 */
export function createOsmTileLayer() {
  return L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors',
    subdomains: ['a', 'b', 'c'],
  });
}

/**
 * CartoDB Voyager tile layer — extremely clear, modern open-source basemap.
 */
export function createCartoTileLayer() {
  return L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
    maxZoom: 19,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: 'abcd',
  });
}

/**
 * ESRI ArcGIS World Street Map — 100% free of cost, zero API key, rock solid CDN, zero 403 blocks.
 * Perfect high-definition streets, state highways, and landmark rendering across India.
 */
export function createEsriTileLayer() {
  return L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}', {
    maxZoom: 19,
    attribution: 'Tiles &copy; Esri &mdash; National Geographic, DeLorme, NAVTEQ',
  });
}

/**
 * OpenStreetMap France / Humanitarian OpenStreetMap Team (HOT) tile layer.
 * 100% free, fast, high contrast, zero API key.
 */
export function createOsmHotTileLayer() {
  return L.tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Tiles style by Humanitarian OpenStreetMap Team',
    subdomains: ['a', 'b', 'c'],
  });
}

/**
 * Pulsing live location marker for desktop/mobile current user.
 */
export function createLiveLocationIcon() {
  return L.divIcon({
    className: 'live-location-pin',
    html: `
      <div style="position: relative; display: flex; align-items: center; justify-content: center; width: 40px; height: 40px;">
        <div style="position: absolute; width: 38px; height: 38px; border-radius: 50%; background: rgba(37, 99, 235, 0.35); animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;"></div>
        <div style="position: relative; width: 26px; height: 26px; border-radius: 50%; background: #2563eb; border: 3px solid #ffffff; box-shadow: 0 4px 10px rgba(0,0,0,0.35); display: flex; align-items: center; justify-content: center; color: white; font-weight: 800; font-size: 11px;">
          📍
        </div>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}

/**
 * Custom numbered stop marker for visit stops.
 */
export function createStopIcon(stopNumber: number | string, status = 'planned') {
  const bg =
    status === 'completed'
      ? '#10b981'
      : status === 'checked_in'
        ? '#3b82f6'
        : status === 'cancelled'
          ? '#ef4444'
          : '#f59e0b';
  return L.divIcon({
    className: 'stop-pin',
    html: `
      <div style="position: relative; display: flex; flex-direction: column; align-items: center;">
        <div style="width: 30px; height: 30px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); background: ${bg}; border: 2px solid #ffffff; box-shadow: 0 3px 8px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
          <span style="transform: rotate(45deg); color: #ffffff; font-size: 11px; font-weight: 800;">${stopNumber}</span>
        </div>
      </div>
    `,
    iconSize: [30, 36],
    iconAnchor: [15, 34],
    popupAnchor: [0, -32],
  });
}
